import { useEffect } from 'react'

const SECTIONS = [
  {
    title: 'Global shortcuts',
    icon: '⌨',
    items: [
      { keys: ['?'], desc: 'Open this shortcuts & features panel from anywhere on the site' },
      { keys: ['Ctrl/⌘', '/'], desc: 'Open search — find any piece or writer instantly' },
      { keys: ['Esc'], desc: 'Close any open modal, overlay, or focus mode' },
    ],
  },
  {
    title: 'Writing',
    icon: '✎',
    items: [
      { keys: ['Ctrl/⌘', 'Enter'], desc: 'Publish your piece instantly from anywhere on the write page' },
      { keys: ['Ctrl/⌘', 'B'], desc: 'Bold selected text in the editor' },
      { keys: ['Ctrl/⌘', 'I'], desc: 'Italic selected text' },
      { keys: ['Ctrl/⌘', 'U'], desc: 'Underline selected text' },
      { keys: ['Ctrl/⌘', 'K'], desc: 'Insert a hyperlink — select text first' },
      { keys: ['Tab'], desc: 'Indent — adds four spaces inside the editor body' },
      { keys: ['Esc'], desc: 'Exit focus mode and return to the full write view' },
      { keys: ['Select text', '→ toolbar'], desc: 'A floating toolbar appears when you select text — bold, italic, H2, H3, pull quote, link, divider' },
    ],
  },
  {
    title: 'Feed & reading',
    icon: '◈',
    items: [
      { keys: ['Click post'], desc: 'Expand a post inline — reads without leaving the feed' },
      { keys: ['↑ Collapse'], desc: 'Collapse the open post and scroll back to the card' },
      { keys: ['◇ Save'], desc: 'Save a post to your reading list — Dashboard → Reading List' },
      { keys: ['⤴ Share'], desc: 'Copy a direct link to the post to your clipboard' },
      { keys: ['◐ Notes'], desc: 'Toggle marginal annotations on the post — left by readers on specific lines' },
      { keys: ['Select text (post)'], desc: 'Select any text inside a published post to leave a marginal note on that exact line' },
    ],
  },
  {
    title: 'Reactions — what they mean',
    icon: '↻',
    items: [
      { keys: ['◐  Sits With Me'], desc: 'I\'ll be thinking about this later — earns the author 3 CI (×2 if post is 30+ days old)' },
      { keys: ['↻  Shifts Something'], desc: 'Changed how I see this — 4 CI, highest-weight automatic reaction' },
      { keys: ['◉  Needed Saying'], desc: 'Someone had to write this — 3 CI' },
      { keys: ['⇌  I Push Back'], desc: 'Disagree — but it deserves the argument — 2 CI' },
      { keys: ['◈  Brought Me Here'], desc: 'Why I come to The Margins — 5 CI, rarest and most meaningful' },
    ],
  },
  {
    title: 'Argument threads',
    icon: '⇌',
    items: [
      { keys: ['Write a Response'], desc: 'On any published post — write a full response piece linked to the original. Both pieces appear together as a thread.' },
      { keys: ['Thread view'], desc: 'All responses to a piece are listed below it under "The Thread"' },
    ],
  },
  {
    title: 'Prompts',
    icon: '◉',
    items: [
      { keys: ['/prompts'], desc: 'Weekly writing prompts — pick one and respond with a full piece. All responses collect under the prompt.' },
      { keys: ['Write a Response (prompt)'], desc: 'Your piece is tagged to the prompt and appears in the prompt\'s response collection' },
    ],
  },
  {
    title: 'Cold Open',
    icon: '◐',
    items: [
      { keys: ['Cold Open toggle'], desc: 'In the write sidebar — publish a piece anonymously. Your name won\'t appear.' },
      { keys: ['Claim this piece'], desc: 'On the post page — if you published anonymously, claim the piece to attach your name to it' },
    ],
  },
  {
    title: 'The Press — CI & ranks',
    icon: '◆',
    items: [
      { keys: ['Column Inches (CI)'], desc: 'The rank currency — earned by publishing, receiving reactions, and getting comments' },
      { keys: ['CI aging'], desc: 'Reactions on posts older than 7 days earn 1.2× CI. 30+ days: 1.5×. 90+ days: 2×. Timeless writing is rewarded.' },
      { keys: ['The Record'], desc: 'Every writer has a public record at /record/:id — full CI history, rank timeline, all 12 Press tiers' },
      { keys: ['The Voice ◆'], desc: 'Rank 12 — editorial appointment only. Cannot be earned automatically.' },
    ],
  },
  {
    title: 'Dashboard features',
    icon: '◌',
    items: [
      { keys: ['✎ Edit'], desc: 'Edit any published or draft post — updates live immediately' },
      { keys: ['Export'], desc: 'Dashboard → Export — download all your published pieces as plain text or Markdown' },
      { keys: ['Reading List'], desc: 'Dashboard → Reading List — all pieces you\'ve saved with ◇' },
      { keys: ['The Pressroom'], desc: 'Your Press card and rank theme — customise how your byline looks' },
      { keys: ['Resonance'], desc: 'Dashboard → Resonance — weighted engagement score per piece. Not views — whether it actually landed.' },
    ],
  },
  {
    title: 'Navigation',
    icon: '→',
    items: [
      { keys: ['/feed'], desc: 'The main feed — all published pieces' },
      { keys: ['/prompts'], desc: 'Weekly writing prompts and collected responses' },
      { keys: ['/community'], desc: 'The Press leaderboard and rank system explained' },
      { keys: ['/masthead'], desc: 'The Masthead — founders, The Voice, top writers' },
      { keys: ['/record/:id'], desc: 'A writer\'s full CI history and rank progression' },
      { keys: ['/writer/:id'], desc: 'A writer\'s public profile and all their pieces' },
    ],
  },
]

export default function KeybindsModal({ onClose }) {
  useEffect(() => {
    const handler = e => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', handler)
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', handler)
      document.body.style.overflow = ''
    }
  }, [onClose])

  return (
    <div style={s.overlay} onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div style={s.modal}>
        <div style={s.header}>
          <div>
            <p style={s.eyebrow}>The Margins</p>
            <h2 style={s.title}>Shortcuts & Features</h2>
            <p style={s.subtitle}>Everything the platform can do. Press <kbd style={s.kbdInline}>?</kbd> to open this anytime.</p>
          </div>
          <button style={s.closeBtn} onClick={onClose} title="Close (Esc)">✕</button>
        </div>

        <div style={s.body}>
          {SECTIONS.map(section => (
            <div key={section.title} style={s.section}>
              <div style={s.sectionHeader}>
                <span style={s.sectionIcon}>{section.icon}</span>
                <span style={s.sectionTitle}>{section.title}</span>
              </div>
              <div style={s.items}>
                {section.items.map((item, i) => (
                  <div key={i} style={s.item}>
                    <div style={s.keys}>
                      {item.keys.map((k, ki) => (
                        <span key={ki} style={s.keyGroup}>
                          <kbd style={s.key}>{k}</kbd>
                          {ki < item.keys.length - 1 && <span style={s.plus}>+</span>}
                        </span>
                      ))}
                    </div>
                    <p style={s.desc}>{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          ))}

          <div style={s.footer}>
            <p style={s.footerText}>
              <strong style={{ color: 'var(--ink)' }}>The Margins</strong> — the place for everything you never said.
              One rule: mean it. Your work is yours — export it anytime from the dashboard.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

const s = {
  overlay: {
    position: 'fixed', inset: 0,
    background: 'rgba(13,11,10,0.88)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 9998, padding: '1rem',
    backdropFilter: 'blur(4px)',
  },
  modal: {
    background: 'var(--paper)',
    border: '1px solid var(--rule)',
    borderRadius: 14,
    width: '100%', maxWidth: 700,
    maxHeight: '90vh',
    display: 'flex', flexDirection: 'column',
    overflow: 'hidden',
    boxShadow: '0 24px 80px rgba(0,0,0,0.6)',
  },
  header: {
    display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
    padding: '1.75rem 2rem 1.25rem',
    borderBottom: '1px solid var(--rule)',
    flexShrink: 0,
  },
  eyebrow: { fontFamily: "'DM Mono', monospace", fontSize: '0.58rem', letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--accent)', marginBottom: '0.4rem' },
  title: { fontFamily: "'Space Grotesk', sans-serif", fontSize: '1.4rem', fontWeight: 700, letterSpacing: '-0.01em', color: 'var(--ink)', marginBottom: '0.3rem' },
  subtitle: { fontFamily: "'Space Grotesk', sans-serif", fontSize: '0.82rem', color: 'var(--muted)', lineHeight: 1.5 },
  kbdInline: { fontFamily: "'DM Mono', monospace", fontSize: '0.7rem', background: 'var(--cream)', border: '1px solid var(--rule)', borderBottom: '2px solid var(--muted)', borderRadius: 4, padding: '0.1rem 0.4rem', color: 'var(--ink)', fontStyle: 'normal' },
  closeBtn: { background: 'none', border: '1px solid var(--rule)', color: 'var(--muted)', width: 32, height: 32, borderRadius: 8, cursor: 'pointer', fontFamily: "'DM Mono', monospace", fontSize: '0.65rem', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 2, transition: 'all 0.15s' },
  body: { overflowY: 'auto', padding: '1.25rem 2rem 2rem', display: 'flex', flexDirection: 'column', gap: '1.5rem' },
  section: {},
  sectionHeader: { display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem', paddingBottom: '0.5rem', borderBottom: '1px solid var(--rule)' },
  sectionIcon: { fontSize: '0.9rem', color: 'var(--accent)' },
  sectionTitle: { fontFamily: "'DM Mono', monospace", fontSize: '0.6rem', letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--accent)', fontWeight: 600 },
  items: { display: 'flex', flexDirection: 'column', gap: '0.4rem' },
  item: { display: 'flex', alignItems: 'flex-start', gap: '1rem', padding: '0.55rem 0.75rem', background: 'var(--cream)', borderRadius: 7, border: '1px solid var(--rule)' },
  keys: { display: 'flex', alignItems: 'center', gap: '0.25rem', flexShrink: 0, minWidth: 140, flexWrap: 'wrap' },
  keyGroup: { display: 'flex', alignItems: 'center', gap: '0.25rem' },
  key: { fontFamily: "'DM Mono', monospace", fontSize: '0.6rem', fontWeight: 600, background: 'var(--paper)', border: '1px solid var(--rule)', borderBottom: '2px solid var(--muted)', borderRadius: 5, padding: '0.18rem 0.5rem', color: 'var(--ink)', letterSpacing: '0.04em', whiteSpace: 'nowrap', fontStyle: 'normal' },
  plus: { fontFamily: "'DM Mono', monospace", fontSize: '0.55rem', color: 'var(--muted)' },
  desc: { fontFamily: "'Space Grotesk', sans-serif", fontSize: '0.78rem', color: 'var(--muted)', lineHeight: 1.55, flex: 1, margin: 0 },
  footer: { padding: '1rem', background: 'rgba(200,64,26,0.05)', border: '1px solid rgba(200,64,26,0.15)', borderRadius: 8 },
  footerText: { fontFamily: "'Space Grotesk', sans-serif", fontSize: '0.78rem', color: 'var(--muted)', lineHeight: 1.65, margin: 0 },
}
